import type { QueryClient } from '@tanstack/react-query'
import { isStaleWriteError, isOptlk } from '@/lib/optimistic'
import { ValidationError } from '@/lib/errorReporter'
import { OVERFLOW_LABEL, type ColdroomPlacement } from '@/lib/coldroomPlacement'

/** What a failed `transfer_bins_to_cell` call becomes: a ValidationError with an
 *  operator sentence for the two refusals we know (stale bin, full cell), else
 *  the driver error itself so it reaches the reporter unchanged. A PostgREST
 *  error is never a StaleWriteError; the RPC raises OPTLK (mig 20261021100001). */
export function transferBinsError(error: unknown): unknown {
  if (isStaleWriteError(error) || isOptlk(error)) {
    return new ValidationError('A selected bin was just changed by someone else. Refresh and try again — no bins were moved.')
  }
  if ((error as { code?: string } | null)?.code === '23P01') {
    return new ValidationError('The destination cell is full or holds a pallet — no bins were moved.')
  }
  return error
}

/** The pre-flight checks before a bin move; throws the operator sentence. */
export function assertTransferDestination(
  destRoomId: string,
  dest: ColdroomPlacement | null,
  fromRoomId: string | null,
  fromCell: string | null,
): asserts dest is ColdroomPlacement {
  if (!destRoomId) throw new ValidationError('Pick a destination coldroom')
  if (!dest)       throw new ValidationError('Pick a destination cell, or the overflow')
  if (destRoomId === fromRoomId && dest.kind === 'cell' && dest.cell === fromCell) {
    throw new ValidationError('Destination is the same as the source.')
  }
}

/** Where the bins are coming from, named for the modal's "From" card. A null
 *  `fromCell` means they came off the overflow list; "—" would read as unknown. */
export function transferSourceLabel(roomCode: string | undefined, fromCell: string | null): string {
  if (fromCell) return roomCode ? `${roomCode} / ${fromCell}` : fromCell
  return roomCode ? `${roomCode} / ${OVERFLOW_LABEL.toLowerCase()}` : '—'
}

/** Every coldroom read that a bin move changes. */
export function invalidateColdroomViews(qc: QueryClient): void {
  for (const k of ['coldroom-bins', 'coldroom-bin-occupied', 'coldroom-occupied', 'coldroom-grid', 'coldroom-counts']) {
    qc.invalidateQueries({ queryKey: [k] })
  }
}
